import { defineMessages } from '@formatjs/intl';

export const messages = defineMessages({
	// Camera
	cameraHeaderTitle: {
		id: 'camera.header.title',
		defaultMessage: 'Emotion Companion',
		description: 'Header title for the emotion detector camera page',
	},
	cameraHeaderEmotionLog: {
		id: 'camera.header.emotionLog',
		defaultMessage: '📔 Emotion Log',
		description: 'Button to navigate to the emotion log page from the camera',
	},
	cameraHeroTitleStart: {
		id: 'camera.hero.title.start',
		defaultMessage: 'Feel Better with Emotion-aware Support',
		description: 'Part 1 of the hero title on the camera page',
	},
	cameraHeroDescription: {
		id: 'camera.hero.description',
		defaultMessage:
			'Capture your image, confirm how you feel, get personalized content recommendations, and start a supportive chat in a tone that fits right now.',
		description: 'Description of the emotion detector workflow in the hero section',
	},
	cameraFooterDemo: {
		id: 'camera.footer.demo',
		defaultMessage: 'Built for internal demo • Emotion Companion',
		description: 'Footer text indicating the app is a demo',
	},
	cameraPageTitle: {
		id: 'camera.page.title',
		defaultMessage: 'Emotion Camera',
		description: 'Title for the emotion camera page',
	},
	cameraIntoTitle: {
		id: 'camera.intro.title',
		defaultMessage: '🎭 Emotion Detection Workflow',
		description: 'Title for the introductory card on the camera page',
	},
	cameraIntroDescription: {
		id: 'camera.intro.description',
		defaultMessage:
			'We capture your image to detect your current emotion. Confirm how you feel, then get personalized content recommendations and start a supportive chat.',
		description: 'Description of the workflow on the introductory card',
	},
	cameraIntroExplanation: {
		id: 'camera.intro.explanation',
		defaultMessage:
			'This process helps us understand your current emotional state so we can provide personalized content recommendations and start a conversation in the right tone for you.',
		description: 'Explanation of the purpose of the workflow on the introductory card',
	},
	cameraIntroCTA: {
		id: 'camera.intro.cta',
		defaultMessage: '📸 Ready to start? Click here to begin! →',
		description: 'Call to action button on the introductory card',
	},
	cameraCaptureTitle: {
		id: 'camera.capture.title',
		defaultMessage: '📸 Capture Emotion',
		description: 'Title for the camera capture card',
	},
	cameraCaptureDescription: {
		id: 'camera.capture.description',
		defaultMessage: 'We capture your image to analyze your current emotion.',
		description: 'Description on the camera capture card',
	},
	cameraCaptureCapturing: {
		id: 'camera.capture.capturing',
		defaultMessage: '⏳ Capturing...',
		description: 'Button text while capturing images',
	},
	cameraCaptureCTA: {
		id: 'camera.capture.cta',
		defaultMessage: '📸 Capture & Detect',
		description: 'Button text to start capturing images',
	},
	cameraCapturePreviewAlt: {
		id: 'camera.capture.previewAlt',
		defaultMessage: 'Captured emotion',
		description: 'Alt text for the captured emotion preview image',
	},
	cameraPreviewTitle: {
		id: 'camera.preview.title',
		defaultMessage: '🖼️ Captured Image',
		description: 'Title for the image preview card',
	},
	cameraPreviewDescription: {
		id: 'camera.preview.description',
		defaultMessage: 'Your captured image from the session',
		description: 'Description on the image preview card',
	},
	cameraConfirmTitle: {
		id: 'camera.confirm.title',
		defaultMessage: '🎯 Confirm Emotion',
		description: 'Title for the emotion confirmation card',
	},
	cameraConfirmQuestion: {
		id: 'camera.confirm.question',
		defaultMessage: 'Is this how you feel? {emoji} <b>{emotion}</b>',
		description: 'Question asking the user to confirm the detected emotion',
	},
	cameraConfirmYes: {
		id: 'camera.confirm.yes',
		defaultMessage: '✅ Yes, continue',
		description: 'Button to confirm the detected emotion',
	},
	cameraConfirmSelectedTitle: {
		id: 'camera.confirm.selectedTitle',
		defaultMessage: 'Selected Emotion',
		description: 'Title for the selected emotion section',
	},
	cameraConfirmChangeSelection: {
		id: 'camera.confirm.changeSelection',
		defaultMessage: 'You can change your selection below if needed:',
		description: 'Text allowing the user to change their emotion selection',
	},
	cameraConfirmEmotion: {
		id: 'camera.confirm.emotion',
		defaultMessage: '🎯 Confirm Your Emotion',
		description: 'Title when asking user to confirm detected emotion',
	},
	cameraSelectEmotion: {
		id: 'camera.select.emotion',
		defaultMessage: '🎭 Select Your Emotion',
		description: 'Title when asking user to select an emotion',
	},
	cameraConfirmDetected: {
		id: 'camera.confirm.detected',
		defaultMessage: "✅ Yes, that's how I feel",
		description: 'Button text to confirm the detected emotion',
	},
	cameraConfirmEmotionQuestion: {
		id: 'camera.confirm.emotion.question',
		defaultMessage: 'Is this how you feel?',
		description: 'Question asking user to confirm detected emotion',
	},
	cameraConfirmOrPick: {
		id: 'camera.confirm.orPick.v2',
		defaultMessage: 'or pick another emotion',
		description: 'Text prompting user to select different emotion',
	},
	cameraRecommendationsTitle: {
		id: 'camera.recommendations.title',
		defaultMessage: '🎯 Recommendations',
		description: 'Title for the recommendations card',
	},
	cameraRecommendationsDescription: {
		id: 'camera.recommendations.description',
		defaultMessage: 'Personalized content based on your emotion',
		description: 'Description on the recommendations card',
	},
	cameraRecommendationsLoading: {
		id: 'camera.recommendations.loading',
		defaultMessage: '🤖 Finding the perfect content for you...',
		description: 'Loading text while fetching recommendations',
	},
	cameraRecommendationsOpen: {
		id: 'camera.recommendations.open',
		defaultMessage: '🎧 Open in Calm',
		description: 'Button to open a recommended content item',
	},
	cameraRecommendationsEmpty: {
		id: 'camera.recommendations.empty',
		defaultMessage: 'No recommendations available right now. Try again later!',
		description: 'Message shown when no content recommendations are found',
	},
	cameraChatTitle: {
		id: 'camera.chat.title.v2',
		defaultMessage: '💬 Chat',
		description: 'Title for the chat card, including the current emotion',
	},
	cameraChatDescription: {
		id: 'camera.chat.description',
		defaultMessage: "Talk about how you're feeling",
		description: 'Description on the chat card',
	},
	cameraChatSending: {
		id: 'camera.chat.sending',
		defaultMessage: 'Sending...',
		description: 'Button text while sending a chat message',
	},
	cameraChatSend: {
		id: 'camera.chat.send',
		defaultMessage: 'Send',
		description: 'Button text to send a chat message',
	},
	cameraChatPlaceholder: {
		id: 'camera.chat.placeholder',
		defaultMessage: 'Type your message here...',
		description: 'Placeholder text for the chat input field',
	},
	cameraChatYou: {
		id: 'camera.chat.you',
		defaultMessage: 'You',
		description: 'Label for user messages in chat',
	},
	cameraChatAssistant: {
		id: 'camera.chat.assistant',
		defaultMessage: 'Assistant',
		description: 'Label for assistant messages in chat',
	},
	// Audio Player
	audioPlayerThumbnailAlt: {
		id: 'audioPlayer.thumbnailAlt',
		defaultMessage: 'Audio content thumbnail',
		description: 'Alt text for audio player thumbnail image',
	},
	audioPlayerRewind: {
		id: 'audioPlayer.rewind',
		defaultMessage: 'Rewind 15 seconds',
		description: 'Aria label for rewind button',
	},
	audioPlayerPlay: {
		id: 'audioPlayer.play',
		defaultMessage: 'Play',
		description: 'Aria label for play button',
	},
	audioPlayerPause: {
		id: 'audioPlayer.pause',
		defaultMessage: 'Pause',
		description: 'Aria label for pause button',
	},
	audioPlayerFastForward: {
		id: 'audioPlayer.fastForward',
		defaultMessage: 'Fast-forward 15 seconds',
		description: 'Aria label for fast-forward button',
	},
	audioPlayerStop: {
		id: 'audioPlayer.stop',
		defaultMessage: 'Stop playing',
		description: 'Aria label for stop button',
	},
	audioPlayerSeek: {
		id: 'audioPlayer.seek',
		defaultMessage: 'Seek audio position',
		description: 'Aria label for seek bar',
	},
	// Home
	homeTitle: {
		id: 'emotionDetector.home.title',
		defaultMessage: 'Emotion Detector',
		description: 'Title for the emotion detector home page',
	},
	homeHeroTitle: {
		id: 'emotionDetector.home.heroTitle',
		defaultMessage: '🎭 Emotion Companion',
		description: 'Main hero title on the home page',
	},
	homeHeroDescription: {
		id: 'emotionDetector.home.heroDescription',
		defaultMessage:
			'Discover your emotions through AI-powered detection and build a personal journal of your emotional journey',
		description: 'Hero description text on the home page',
	},
	question: {
		id: 'emotionDetector.home.question',
		defaultMessage: 'What would you like to do?',
		description: 'Main heading on the emotion detector landing page',
	},
	openDetector: {
		id: 'emotionDetector.home.openDetector',
		defaultMessage: 'Open Emotion Detector',
		description: 'Button text to navigate to the emotion detector camera page',
	},
	openLog: {
		id: 'emotionDetector.home.openLog',
		defaultMessage: 'Open Emotion Log',
		description: 'Button text to navigate to the emotion log page',
	},
	homeHelpText: {
		id: 'emotionDetector.home.helpText',
		defaultMessage:
			'💡 Start with the Emotion Detector to capture your current mood, then visit your Emotion Log to track patterns over time',
		description: 'Helpful instruction text on the home page',
	},
	// Signup
	signupTitle: {
		id: 'emotionDetector.signup.title',
		defaultMessage: 'Emotion Detector Sign Up - Private',
		description: 'Title for the emotion detector signup page',
	},
	signupHeader: {
		id: 'emotionDetector.signup.header',
		defaultMessage: 'Sign up for Emotion Detector',
		description: 'Header for the emotion detector signup page',
	},
	// Emotion Log
	logHeaderTitle: {
		id: 'log.header.title',
		defaultMessage: 'Emotion Companion',
		description: 'Header title for the emotion log page',
	},
	backToCamera: {
		id: 'log.header.backToCamera',
		defaultMessage: '📸 Back to Camera',
		description: 'Button to navigate back to the camera page from the log',
	},
	pageTitle: {
		id: 'log.page.title',
		defaultMessage: 'Emotion Journal - Private',
		description: 'Title for the emotion journal page',
	},
	mainTitle: {
		id: 'log.main.title',
		defaultMessage: '📔 Your Emotion Journal',
		description: 'Main title on the emotion journal page',
	},
	loading: {
		id: 'log.loading',
		defaultMessage: 'Loading journal entries...',
		description: 'Loading text while fetching journal entries',
	},
	emptyTitle: {
		id: 'log.empty.title',
		defaultMessage: 'No journal entries found.',
		description: 'Text shown when no journal entries exist',
	},
	emptyPrompt: {
		id: 'log.empty.prompt',
		defaultMessage: 'Start by capturing some emotions with the camera!',
		description: 'Prompt to the user to use the camera when the log is empty',
	},
	entryCorrected: {
		id: 'log.entry.corrected',
		defaultMessage: '(corrected to: {annotation})',
		description: 'Text indicating the user corrected the detected emotion to their own annotation',
	},
	entryCapturedImages: {
		id: 'log.entry.capturedImages',
		defaultMessage: 'Captured Images',
		description: 'Label for the section displaying captured images in the journal entry',
	},
	logEntryYourNotes: {
		id: 'log.entry.yourNotes',
		defaultMessage: 'Your notes:',
		description: 'Label for the user notes textarea in a journal entry.',
	},
	logDeleteDialogTitle: {
		id: 'log.deleteDialog.title',
		defaultMessage: 'Delete Journal Entry',
		description: 'Title for the delete confirmation dialog.',
	},
	logDeleteDialogMessage: {
		id: 'log.deleteDialog.message',
		defaultMessage: 'Are you sure you want to delete this journal entry? This action cannot be undone.',
		description: 'Confirmation message in the delete dialog.',
	},
	logDeleteDialogCancel: {
		id: 'log.deleteDialog.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button in the delete dialog.',
	},
	logDeleteDialogDelete: {
		id: 'log.deleteDialog.delete',
		defaultMessage: 'Delete',
		description: 'Delete button in the delete dialog.',
	},
	// Emotion Detector Header
	headerTitle: {
		id: 'emotionDetector.header.title',
		defaultMessage: 'Emotion Companion',
		description: 'Header title for emotion detector pages',
	},
	cameraButton: {
		id: 'emotionDetector.header.camera',
		defaultMessage: '📸 Back to Camera',
		description: 'Button to navigate to camera page',
	},
	logButton: {
		id: 'emotionDetector.header.log',
		defaultMessage: '📔 Emotion Log',
		description: 'Button to navigate to log page',
	},
	deleteData: {
		id: 'emotionDetector.profile.deleteData',
		defaultMessage: 'Delete My Data',
		description: 'Button to delete user data',
	},
	signOut: {
		id: 'emotionDetector.profile.signOut',
		defaultMessage: 'Sign Out',
		description: 'Button to sign out',
	},
	// Password Gate
	passwordTitle: {
		id: 'emotionDetector.password.title',
		defaultMessage: 'Enter Password',
		description: 'Title for the password protection screen',
	},
	passwordPlaceholder: {
		id: 'emotionDetector.password.placeholder',
		defaultMessage: 'Password',
		description: 'Placeholder text for password input field',
	},
	passwordUnlock: {
		id: 'emotionDetector.password.unlock',
		defaultMessage: 'Unlock',
		description: 'Button text to unlock the password protected page',
	},
});

export default messages;
